import * as React from "react";
import "jsuites/dist/jsuites.css";
import "jspreadsheet-ce/dist/jspreadsheet.css";
import jspreadsheet from "jspreadsheet-ce/dist/index";
import { useEffect, useRef } from "react";

interface SpreadsheetProps<
  T extends jspreadsheet.CellValue[] | Record<string, jspreadsheet.CellValue>,
> {
  data: T[];
  columns: string[];
  hiddenColumns?: string[];
  rowHeaders?: boolean;
  height?: number;
  width?: number;
  readOnly?: boolean;
}

export function Spreadsheet<
  T extends jspreadsheet.CellValue[] | Record<string, jspreadsheet.CellValue>,
>(props: SpreadsheetProps<T>) {
  const { data, columns, hiddenColumns, rowHeaders, height, width, readOnly } =
    props;
  const [worksheets, setWorksheets] = React.useState<
    jspreadsheet.WorksheetInstance[]
  >([]);
  const divRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!divRef.current) {
      return;
    }
    const worksheets = jspreadsheet(divRef.current, {
      about: false,
      onload: (spreadsheet) => {
        const worksheet = spreadsheet.worksheets[0];
        const tempDiv = document.createElement("div");
        tempDiv.style.position = "absolute";
        tempDiv.style.visibility = "hidden";
        tempDiv.style.whiteSpace = "nowrap";
        document.body.appendChild(tempDiv);
        for (let i = 0; i < columns.length; i++) {
          let width = 50;
          for (let j = 0; j < data.length; j++) {
            const cell = worksheet.getCellFromCoords(i, j);
            tempDiv.textContent = cell.innerText || cell.textContent;
            width = Math.max(width, tempDiv.offsetWidth + 10);
          }
          tempDiv.textContent = columns[i];
          width = Math.max(width, tempDiv.offsetWidth + 10);
          worksheet.setWidth(i, width);
        }
        document.body.removeChild(tempDiv);
        if (!rowHeaders) {
          worksheet.hideIndex();
        }
      },
      worksheets: [
        {
          data: data as jspreadsheet.CellValue[][],
          columns: columns.map((_) => ({
            name: _,
            title: _,
            align: "right",
            readOnly: readOnly,
          })),
          search: true,
          pagination: 100,
          paginationOptions: [10, 100, 1000, 10000],
          allowComments: false,
          allowDeleteColumn: false,
          allowDeleteRow: false,
          allowInsertColumn: false,
          allowInsertRow: false,
          allowManualInsertColumn: false,
          allowManualInsertRow: false,
          allowRenameColumn: false,
          columnDrag: false,
          csvDelimiter: ",",
          csvFileName: "export",
          tableHeight: height,
          tableWidth: width,
          tableOverflow: height != undefined || width != undefined,
        },
      ],
    });
    if (hiddenColumns) {
      const worksheet = worksheets[0];
      hiddenColumns.forEach((col) => {
        const index = columns.indexOf(col);
        if (index !== -1) {
          worksheet.hideColumn(index);
        }
      });
    }
    setWorksheets(worksheets);
    return () => {
      if (!divRef.current) {
        return;
      }
      jspreadsheet.destroy(divRef.current as any);
    };
  }, [data]);

  useEffect(() => {
    if (hiddenColumns && worksheets.length > 0) {
      const worksheet = worksheets[0];
      columns.forEach((col, index) => {
        if (index >= worksheet.cols.length) {
          return;
        }
        if (hiddenColumns.includes(col)) {
          worksheet.hideColumn(index);
        } else {
          worksheet.showColumn(index);
        }
      });
    }
  }, [JSON.stringify(columns), JSON.stringify(hiddenColumns)]);

  return <div ref={divRef} />;
}
